import { ContentfulContentTypes, ContentfulService } from '../services/contentful.service';
import { Entry, EntryFieldTypes, EntrySkeletonType } from 'contentful';
import { Service } from 'typedi';
import { PricingTierType } from '@enterprise/common';

export type AnalyticsAdvertisementSkeleton = EntrySkeletonType<
    {
        advertisementBody: EntryFieldTypes.RichText;
        advertisementImage: EntryFieldTypes.AssetLink;
        header: EntryFieldTypes.Symbol;
        learnMoreLink: EntryFieldTypes.Symbol;
        upgradeLink: EntryFieldTypes.Symbol;
        pricingTier: EntryFieldTypes.Symbol;
    },
    ContentfulContentTypes.AnalyticsAdvertisement
>;

export type AnalyticsAdvertisement = Entry<AnalyticsAdvertisementSkeleton, undefined, string>['fields'];

interface OfflineAdvertisementData {
    [key: string]: {
        dismissed: string[];
    };
}

type AdvertisementEntry = Entry<AnalyticsAdvertisementSkeleton, undefined, string>;

@Service()
export class AdvertisementStore {
    private readonly localStorageKey = 'EnterpriseAdvertisements';

    private readonly contentfulService: ContentfulService;

    private readonly sectionKeys = new Map<ContentfulContentTypes, string>().set(ContentfulContentTypes.AnalyticsAdvertisement, 'analytics');

    private contentType: ContentfulContentTypes = ContentfulContentTypes.AnalyticsAdvertisement;

    private displayableAdvertisements: AdvertisementEntry[] = [];

    private offlineData: OfflineAdvertisementData;

    constructor() {
        this.contentfulService = new ContentfulService();
    }

    async getRandomAdForPricingTier(pricingTier: PricingTierType): Promise<AnalyticsAdvertisement | null> {
        this.loadOfflineData();
        await this.loadRemoteData(pricingTier);
        if (this.displayableAdvertisements.length) {
            const randomEntryIndex = Math.floor(Math.random() * this.displayableAdvertisements.length);
            return this.displayableAdvertisements[randomEntryIndex].fields;
        }
        return null;
    }

    dismissDisplayableAds(): void {
        this.loadOfflineData();

        const advertisementIds = this.displayableAdvertisements.map((entry) => entry.sys.id);
        const sectionKey = this.getOfflineDataSectionKey();
        const { dismissed } = this.offlineData[sectionKey];
        this.offlineData[sectionKey].dismissed = [...new Set([...dismissed, ...advertisementIds])];

        this.updateLocalStorage(this.offlineData);
        this.displayableAdvertisements = [];
    }

    private getDataFromLocalStorage(): OfflineAdvertisementData | null {
        const data = localStorage.getItem(this.localStorageKey);

        if (data) {
            return JSON.parse(data);
        }

        return null;
    }

    private updateLocalStorage(newData): void {
        localStorage.setItem(this.localStorageKey, JSON.stringify(newData));
    }

    private async loadRemoteData(pricingTier: PricingTierType): Promise<void> {
        const query = {
            'fields.pricingTier[match]': pricingTier,
        };

        const remoteData = await this.contentfulService.getEntriesByContentType<AnalyticsAdvertisementSkeleton>(this.contentType, query);

        const sectionKey = this.getOfflineDataSectionKey();
        const { dismissed } = this.offlineData[sectionKey];
        this.displayableAdvertisements = remoteData.filter((entry) => !dismissed.includes(entry.sys.id));
    }

    private getOfflineDataSectionKey(): string {
        const sectionKey = this.sectionKeys.get(this.contentType);
        if (sectionKey) {
            return sectionKey;
        }
        throw new Error(`Section key does not exist for content type ${this.contentType}`);
    }

    private loadOfflineData(): void {
        this.offlineData = this.getDataFromLocalStorage() || { analytics: { dismissed: [] } };
    }
}
